import React, { useState, useRef } from 'react';

const Card: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="bg-white border rounded-xl p-5 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">{icon ?? '⬚'}</div>
      <h2 className="text-base font-semibold">{title}</h2>
    </div>
    <div className="grid grid-cols-12 gap-3">{children}</div>
  </div>
);

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-[13px] text-gray-700 mb-1">{children}</label>
);

const Field: React.FC<{ span?: string; children: React.ReactNode; label?: string }> = ({ span = "col-span-12 sm:col-span-6 lg:col-span-4", children, label }) => (
  <div className={span}>
    {label ? <Label>{label}</Label> : null}
    {children}
  </div>
);

const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} className={"w-full h-9 px-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 " + (props.className || "")} />
);

const Textarea = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...props} className={"w-full min-h-[72px] px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 " + (props.className || "")} />
);

const INDUSTRIES = [
  "Accommodation & Food Services",
  "Administrative & Support Services",
  "Agriculture / Forestry / Fishing / Hunting",
  "Arts / Entertainment / Recreation",
  "Construction",
  "Consulting / Professional Services",
  "Education",
  "Energy / Utilities",
  "Finance / Insurance",
  "Government / Public Administration",
  "Healthcare / Social Assistance",
  "Information Technology / Software",
  "Manufacturing",
  "Mining / Oil & Gas",
  "Nonprofit / NGO",
  "Pharmaceuticals / Biotechnology",
  "Real Estate / Rental / Leasing",
  "Retail Trade",
  "Transportation / Warehousing",
  "Wholesale Trade",
  "Other"
];

export default function CompanyInformationSection() {
  // Company form fields state
  const [companyName, setCompanyName] = useState("TechCorp Solutions");
  const [industry, setIndustry] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [companyDescription, setCompanyDescription] = useState("We are a growing hospitality group operating two high‑volume concepts.");
  
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [welcomeVideo, setWelcomeVideo] = useState<string | null>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [videoTrim, setVideoTrim] = useState<{ start: number; end: number } | null>(null);
  const [showLogoEditor, setShowLogoEditor] = useState(false);
  const [logoZoom, setLogoZoom] = useState(1);
  const [logoPan, setLogoPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [tempLogo, setTempLogo] = useState<string | null>(null); // Temporary logo for editing

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setTempLogo(reader.result as string);
        setShowLogoEditor(true);
        // Reset zoom and pan for new upload
        setLogoZoom(1);
        setLogoPan({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setWelcomeVideo(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleLogoDoubleClick = () => {
    if (companyLogo) {
      setTempLogo(companyLogo);
      setShowLogoEditor(true);
    }
  };

  const handleZoomIn = () => setLogoZoom(prev => Math.min(prev + 0.1, 5));
  const handleZoomOut = () => setLogoZoom(prev => Math.max(prev - 0.1, 0.3));
  
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - logoPan.x, y: e.clientY - logoPan.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setLogoPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetLogoEditor = () => {
    setLogoZoom(1);
    setLogoPan({ x: 0, y: 0 });
  };

  const cropLogoToSquare = () => {
    if (!tempLogo) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Output square size
      const outputSize = 400;
      canvas.width = outputSize;
      canvas.height = outputSize;

      // Container dimensions (the visible square area in the preview)
      const containerSize = 256;
      
      // Calculate initial object-contain scale
      // The preview image uses object-contain, so we need to account for that
      const imageAspect = img.width / img.height;
      let displayWidth, displayHeight;
      
      if (imageAspect > 1) {
        // Wider than tall - constrained by width
        displayWidth = containerSize;
        displayHeight = containerSize / imageAspect;
      } else {
        // Taller than wide - constrained by height
        displayHeight = containerSize;
        displayWidth = containerSize * imageAspect;
      }
      
      // Apply user's zoom to the display dimensions
      const zoomedWidth = displayWidth * logoZoom;
      const zoomedHeight = displayHeight * logoZoom;
      
      // Calculate position after centering and pan
      const displayX = (containerSize - zoomedWidth) / 2 + logoPan.x;
      const displayY = (containerSize - zoomedHeight) / 2 + logoPan.y;
      
      // The visible area in display coordinates
      const visibleLeft = 0;
      const visibleTop = 0;
      const visibleRight = containerSize;
      const visibleBottom = containerSize;
      
      // Convert visible area to image coordinates
      // What part of the zoomed image is visible?
      const cropLeft = visibleLeft - displayX;
      const cropTop = visibleTop - displayY;
      const cropRight = visibleRight - displayX;
      const cropBottom = visibleBottom - displayY;
      
      // Convert from display pixels to original image pixels
      const scale = img.width / displayWidth; // How many image pixels per display pixel
      const sourceX = (cropLeft / logoZoom) * scale;
      const sourceY = (cropTop / logoZoom) * scale;
      const sourceWidth = ((cropRight - cropLeft) / logoZoom) * scale;
      const sourceHeight = ((cropBottom - cropTop) / logoZoom) * scale;

      // Clamp to image bounds
      const clampedSourceX = Math.max(0, Math.min(sourceX, img.width));
      const clampedSourceY = Math.max(0, Math.min(sourceY, img.height));
      const clampedSourceWidth = Math.min(sourceWidth, img.width - clampedSourceX);
      const clampedSourceHeight = Math.min(sourceHeight, img.height - clampedSourceY);

      // Fill with white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, outputSize, outputSize);
      
      // Draw the cropped portion centered on the canvas
      const aspectRatio = clampedSourceWidth / clampedSourceHeight;
      let drawWidth, drawHeight, drawX, drawY;
      
      if (aspectRatio > 1) {
        drawWidth = outputSize;
        drawHeight = outputSize / aspectRatio;
        drawX = 0;
        drawY = (outputSize - drawHeight) / 2;
      } else {
        drawHeight = outputSize;
        drawWidth = outputSize * aspectRatio;
        drawX = (outputSize - drawWidth) / 2;
        drawY = 0;
      }
      
      ctx.drawImage(
        img,
        clampedSourceX,
        clampedSourceY,
        clampedSourceWidth,
        clampedSourceHeight,
        drawX,
        drawY,
        drawWidth,
        drawHeight
      );

      // Convert to data URL and save
      const croppedImage = canvas.toDataURL('image/png', 1.0);
      setCompanyLogo(croppedImage);
      setShowLogoEditor(false);
      setTempLogo(null);
      
      // Reset for next time
      setLogoZoom(1);
      setLogoPan({ x: 0, y: 0 });
    };
    
    img.src = tempLogo;
  };

  // Save company data to localStorage whenever it changes
  React.useEffect(() => {
    const companyData = {
      companyName,
      industry,
      companyWebsite,
      companyAddress,
      contactEmail,
      companyDescription,
      companyLogo,
      welcomeVideo,
    };
    try {
      localStorage.setItem('passcom-company-data', JSON.stringify(companyData));
      // Dispatch event so other components can react to changes
      window.dispatchEvent(new CustomEvent('passcom-company-updated', { detail: companyData }));
    } catch (error) {
      console.error('Failed to save company data:', error);
    }
  }, [companyName, industry, companyWebsite, companyAddress, contactEmail, companyDescription, companyLogo, welcomeVideo]);

  // Load company data on mount
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem('passcom-company-data');
      if (saved) {
        const data = JSON.parse(saved);
        if (data.companyName) setCompanyName(data.companyName);
        if (data.industry) setIndustry(data.industry);
        if (data.companyWebsite) setCompanyWebsite(data.companyWebsite);
        if (data.companyAddress) setCompanyAddress(data.companyAddress);
        if (data.contactEmail) setContactEmail(data.contactEmail);
        if (data.companyDescription) setCompanyDescription(data.companyDescription);
        if (data.companyLogo) setCompanyLogo(data.companyLogo);
        if (data.welcomeVideo) setWelcomeVideo(data.welcomeVideo);
      }
    } catch (error) {
      console.error('Failed to load company data:', error);
    }
  }, []);

  return (
    <>
      {/* Form Content - full width */}
      <Card title="Company Information" icon={<span>🏢</span>}>
        <Field span="col-span-12 lg:col-span-6" label="Company Name">
          <Input 
            placeholder="e.g., TechCorp Solutions" 
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </Field>

        <Field span="col-span-12 lg:col-span-6" label="Industry">
          <select 
            className="w-full h-9 px-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
          >
            <option value="">Select industry...</option>
            {INDUSTRIES.map((ind) => (
              <option key={ind} value={ind}>
                {ind}
              </option>
            ))}
          </select>
        </Field>

        <Field span="col-span-12 lg:col-span-6" label="Company Website">
          <Input 
            placeholder="https://www.example.com" 
            type="url"
            value={companyWebsite}
            onChange={(e) => setCompanyWebsite(e.target.value)}
          />
        </Field>

        <Field span="col-span-12 lg:col-span-6" label="Company Address">
          <Input 
            placeholder="e.g., 123 Main St, Boston, MA 02101"
            value={companyAddress}
            onChange={(e) => setCompanyAddress(e.target.value)}
          />
        </Field>

        <Field span="col-span-12 lg:col-span-6" label="Contact Email">
          <Input 
            placeholder="e.g., hiring@company.com" 
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
        </Field>

        <Field span="col-span-12" label="">
          <div className="flex gap-6 items-start">
            {/* Logo */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[13px] text-gray-700">Logo</span>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  className="text-blue-600 hover:underline text-sm"
                >
                  Upload
                </button>
              </div>
              {companyLogo ? (
                <div
                  className="relative w-24 h-24 border rounded-lg overflow-hidden bg-white cursor-pointer hover:border-blue-400"
                  onDoubleClick={handleLogoDoubleClick}
                  title="Double-click to re-crop"
                >
                  <img src={companyLogo} alt="Company Logo" className="w-full h-full object-contain" />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCompanyLogo(null);
                    }}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 z-10"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400">
                  <span className="text-2xl">🏢</span>
                </div>
              )}
            </div>

            {/* Welcome Video */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[13px] text-gray-700">Welcome Video</span>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  className="text-blue-600 hover:underline text-sm"
                >
                  Upload
                </button>
                {welcomeVideo && (
                  <>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setWelcomeVideo(null)}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
              {welcomeVideo ? (
                <div className="border rounded-lg overflow-hidden bg-black cursor-pointer" style={{ width: '192px', height: '96px' }} onClick={() => setShowVideoEditor(true)} title="Click to edit">
                  <video
                    src={welcomeVideo}
                    controls
                    className="w-full h-full object-cover"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400" style={{ width: '192px', height: '96px' }}>
                  <div className="text-center">
                    <div className="text-2xl mb-1">🎥</div>
                    <div className="text-xs">No video</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Field>

        <Field span="col-span-12" label="Company Description">
          <Textarea 
            placeholder="Tell applicants about your company…" 
            value={companyDescription}
            onChange={(e) => setCompanyDescription(e.target.value)}
          />
        </Field>
      </Card>

      {/* Logo Editor Modal */}
      {showLogoEditor && tempLogo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-2">Crop Logo to Square</h3>
            <p className="text-sm text-gray-600 mb-4">Drag to reposition, use zoom controls to adjust</p>
            <div className="relative w-64 h-64 mx-auto mb-4">
              {/* Container with square crop area */}
              <div
                className="relative w-full h-full border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-100"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              >
                <img
                  src={tempLogo}
                  alt="Logo Preview"
                  style={{
                    transform: `scale(${logoZoom}) translate(${logoPan.x / logoZoom}px, ${logoPan.y / logoZoom}px)`,
                    transformOrigin: 'center',
                    transition: isDragging ? 'none' : 'transform 0.1s'
                  }}
                  className="w-full h-full object-contain select-none"
                  draggable={false}
                />
                
                {/* Square crop overlay guide */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-0 border-2 border-blue-500 rounded-lg shadow-lg"></div>
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-b from-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-t from-black/20 to-transparent"></div>
                  <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-r from-black/20 to-transparent"></div>
                  <div className="absolute top-0 bottom-0 right-0 w-1 bg-gradient-to-l from-black/20 to-transparent"></div>
                </div>
              </div>
              
              {/* Corner indicators */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-blue-500"></div>
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-blue-500"></div>
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-blue-500"></div>
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-blue-500"></div>
            </div>
            
            <div className="flex items-center gap-4 mb-4">
              <button 
                onClick={handleZoomOut} 
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 font-bold text-lg"
              >
                −
              </button>
              <div className="flex-1 text-center">
                <div className="text-sm font-medium text-gray-700">Zoom</div>
                <div className="text-xs text-gray-500">{(logoZoom * 100).toFixed(0)}%</div>
              </div>
              <button 
                onClick={handleZoomIn} 
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 font-bold text-lg"
              >
                +
              </button>
            </div>
            
            <div className="flex gap-2">
              <button 
                onClick={() => {
                  setShowLogoEditor(false);
                  setTempLogo(null);
                }} 
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={resetLogoEditor} 
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Reset
              </button>
              <button 
                onClick={cropLogoToSquare} 
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex-1 font-medium"
              >
                Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Video Trim Editor */}
      {showVideoEditor && welcomeVideo && (
        <VideoEditorModal
          src={welcomeVideo}
          onCancel={()=> setShowVideoEditor(false)}
          onApply={({ start, end }) => {
            setVideoTrim({ start, end });
            setShowVideoEditor(false);
          }}
        />
      )}
    </>
  );
}

export function PostingList() {
  const [items, setItems] = React.useState<{ postingName: string }[]>([]);
  const reload = React.useCallback(() => {
    try {
      const raw = localStorage.getItem('passcom-postings');
      const list = raw ? JSON.parse(raw) : [];
      setItems(list);
    } catch {
      setItems([]);
    }
  }, []);
  React.useEffect(() => {
    reload();
    const onUpd = () => reload();
    window.addEventListener('passcom-postings-updated', onUpd);
    window.addEventListener('storage', onUpd as any);
    return () => {
      window.removeEventListener('passcom-postings-updated', onUpd);
      window.removeEventListener('storage', onUpd as any);
    };
  }, [reload]);
  const loadPosting = (name: string) => {
    try {
      const raw = localStorage.getItem('passcom-postings');
      const list = raw ? JSON.parse(raw) : [];
      const found = list.find((p: any) => p.postingName === name);
      if (found) {
        window.dispatchEvent(new CustomEvent('passcom-load-posting', { detail: found }));
      }
    } catch {}
  };
  const removePosting = (name: string) => {
    try {
      const raw = localStorage.getItem('passcom-postings');
      const list = raw ? JSON.parse(raw) : [];
      const next = list.filter((p: any) => p.postingName !== name);
      localStorage.setItem('passcom-postings', JSON.stringify(next));
      window.dispatchEvent(new CustomEvent('passcom-postings-updated'));
    } catch {}
  };
  if (!items.length) {
    return <div className="text-xs text-gray-500 text-center py-8">No saved postings</div>;
  }
  return (
    <div className="space-y-2">
      {items.map((it) => (
        <div key={it.postingName} className="flex items-center gap-2 justify-between border rounded-lg px-3 py-2 hover:bg-gray-50">
          <div className="text-xs text-gray-700 truncate flex-1" title={it.postingName}>{it.postingName}</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline font-medium"
              onClick={() => loadPosting(it.postingName)}
            >
              Use
            </button>
            <button
              type="button"
              className="text-xs text-gray-500 hover:text-red-600"
              onClick={() => removePosting(it.postingName)}
              title="Remove"
            >
              Del
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ------- Simple Video Trim Modal -------
function VideoEditorModal({ src, onCancel, onApply }: { src: string; onCancel: ()=>void; onApply: (r:{start:number,end:number})=>void }) {
  const videoRef = React.useRef<HTMLVideoElement|null>(null);
  const [start, setStart] = React.useState(0);
  const [end, setEnd] = React.useState(0);
  const onLoaded = () => {
    const d = videoRef.current?.duration || 0;
    setEnd(Math.floor(d));
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-4 w-[520px]">
        <h3 className="text-sm font-semibold mb-2">Edit Video</h3>
        <video ref={videoRef} src={src} controls onLoadedMetadata={onLoaded} className="w-full bg-black rounded" />
        <div className="mt-3 grid grid-cols-2 gap-3 items-center">
          <label className="text-xs text-gray-600">Start (s)</label>
          <input className="border rounded px-2 h-8" type="number" value={start} min={0} max={Math.max(0,end-1)} onChange={(e)=> setStart(Number(e.target.value))}/>
          <label className="text-xs text-gray-600">End (s)</label>
          <input className="border rounded px-2 h-8" type="number" value={end} min={start+1} onChange={(e)=> setEnd(Number(e.target.value))}/>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button className="px-3 py-1 border rounded text-sm" onClick={onCancel}>Cancel</button>
          <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm" onClick={()=> onApply({ start, end })}>Save</button>
        </div>
      </div>
    </div>
  );
}
